import { useState, useMemo } from "react";
import {
    Box,
    Checkbox,
    FormControlLabel,
    Typography
  } from '@mui/material'
import intl from 'components/i18n/ReactIntlWrapper';
import Spinner from '../../Spinner';
import { DataGrid, GridColDef, GridInitialState, GridSortModel } from '@mui/x-data-grid';
import { DEFAULT_PAGINATION } from 'hooks/useSearch';
import { getUserEntity } from 'common/UserEntityProvider';
import { SCOPES } from 'common/permissions';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { formatDate } from "common/utils";
import { usePaginationPreference } from 'hooks/usePaginationPreference';


const ProjectEmployeeList = ({ data, loadingEmployee, handleEmployeeEdit, handleEmployeeDelete, input, setInput, inactiveEmployees, setInactiveEmployees, disabled }) => {
    const handleInactiveEmployees = () => {
        setInactiveEmployees(!inactiveEmployees);
        setInput({ ...input, currentPage: 1 });
    };
    const entityType = getUserEntity();
    const [pagination, setPagination] = usePaginationPreference('projectEmployees', 10);

    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'employee', sort: 'asc' }]);

    const handleSortModelChange = (model: GridSortModel) => {
      setSortModel(model);
    };

    const checkInactiveEmployee = (data: any) => {
      const currentDate = new Date();
      const isEmployeeInactive = !data?.employee?.isActive;

      let isProjectExpired = false;
      if (data?.dueDate) {
        // Parse the dueDate and compare with current date
        const dueDateParsed = new Date(data.dueDate);
        if (!isNaN(dueDateParsed.getTime())) {
          isProjectExpired = dueDateParsed < currentDate;
        }
      }

      return isEmployeeInactive || isProjectExpired;
    };

	  const INITIAL_STATE: GridInitialState = {
	  	pagination: {
	  	  paginationModel: {
	  		page: DEFAULT_PAGINATION.pageNum,
	  		pageSize: DEFAULT_PAGINATION.pageSize,
	  	  },
	  	},
	  	sorting: {
	  	  sortModel: [
	  		{
	  		  field: 'Employee',
	  		  sort: 'asc',
	  		},
	  	  ],
	  	},
	    };

     const columns: GridColDef[] = [
        {
          field: 'employee',
          headerName: intl.get('employeeList.table.thead.employee'),
          width: 250,
          flex: 1,
          valueGetter: (value, row) => `${row.employee?.name || ''} ${row.employee?.lastName || ''}`.trim(),
          renderCell: (params) => (
            <Box display="flex" alignItems="center" gap={1}>
              <span>{params.row.employee.name || ''} {params.row.employee.lastName || ''}</span>
              {checkInactiveEmployee(params.row) && (
                <Typography fontStyle="italic" fontSize={12} color="text.secondary">({intl.get('statusSelect.inactive')})</Typography>
              )}
            </Box>
          ),
        },
        {
          field: 'hourlyRate',
          headerName: intl.get('employeeList.table.thead.hourlyRate'),
          width: 250,
          flex: 1,
        },
        {
          field: 'monthlyRate',
          headerName: intl.get('employeeList.table.thead.monthlyRate'),
          width: 250,
          flex: 1,
        },
        {
          field: 'allocation',
          headerName: intl.get('employeeList.table.thead.allocation'),
          width: 250,
          flex: 1,
          valueGetter: (value, row) => row.allocation,
          renderCell: (params) => <>{params.row.allocation} {params.row.allocationType === 1 ? ' %' : ' hs.'}</>,
        },
        {
          field: 'role',
          headerName: intl.get('employeeList.table.thead.role'),
          width: 250,
          flex: 1,
          valueGetter: (value, row) => row.role?.name || '',
          renderCell: (params) => <>{params.row.role.name || ''}</>,
        },
        {
          field: 'timesheetViewer',
          headerName: intl.get('employeeList.table.thead.timesheetViewer'),
          width: 250,
          flex: 1,
          valueGetter: (value, row) => Boolean(row.isTimesheetViewer),
          renderCell: (params) => (
            <Checkbox
              checked={Boolean(params.row.isTimesheetViewer)}
              disabled
            />
          ),
        },
        {
          field: 'startEndDate',
          headerName: `${intl.get('employeeList.table.thead.startDate')} ${intl.get('employeeList.table.thead.dueDate')}`,
          width: 250,
          flex: 1,
          valueGetter: (value, row) => row.startDate,
          renderCell: (params) => <>{formatDate(new Date(params?.row?.startDate)) || ''}-{formatDate(new Date(params?.row?.dueDate)) || ''}</>,
        },
        {
          field: 'actions',
          headerName: '',
          width: 150,
          sortable: false,
          renderCell: (params) => (
            <Box display="flex" gap={1}>
              <DefaultActions
                row={params.row}
                entityType={entityType.entityType}
                onEdit={() => handleEmployeeEdit(params.row.id)}
                disabled={disabled}
                editScopes={[SCOPES['projects.employees.edit']]}
              />
              <DefaultActions
                row={params.row}
                entityType={entityType.entityType}
                onDelete={() => handleEmployeeDelete(params.row.id)}
                deleteScopes={[SCOPES['projects.employees.delete']]}
                disabled={disabled}
              />
            </Box>
          ),
        },
      ];

    const filteredRows = useMemo(() => {
      const currentDate = new Date();
      const currentDateFormatted = currentDate.toISOString();

      if (!data?.employeeProjects) return [];

      if (inactiveEmployees) {
        return data.employeeProjects;
      }

      return data.employeeProjects.filter((employee) => (employee.dueDate >= currentDateFormatted && employee.employee.isActive));
    }, [data?.employeeProjects, inactiveEmployees]);

    return (
        <Box sx={{ overflowX: 'auto', mt: 2 }}>
          <Box>
            <Box style={{ width: '100%', display: 'flex' }}>
              {loadingEmployee && !data && <Spinner />}
              {data && (
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  rowCount={filteredRows.length}
                  loading={loadingEmployee}
                  initialState={INITIAL_STATE}
                  paginationModel={pagination}
                  onPaginationModelChange={setPagination}
                  sortModel={sortModel as GridSortModel}
                  onSortModelChange={handleSortModelChange}
                  pageSizeOptions={[5, 10, 20]}
                  sortingMode="client"
                  getRowClassName={(params) =>
                    checkInactiveEmployee(params.row) ? 'employee-inactive' : ''
                  }
                  sx={{
                    '& .employee-inactive': {
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
            
          <Box mt={2} ml={1}>
            <FormControlLabel
              control={
                <Checkbox
                  onClick={handleInactiveEmployees}
                  sx={{ ml: 1 }}
                />
              }
              label={intl.get('employeeList.checkbox.inactive')}
            />
          </Box>
        </Box>
    )
}

export default ProjectEmployeeList;